/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import {
  authApi,
  validators,
  ApiError,
  NetworkError,
  TimeoutError,
  type CredentialsUpdatePayload,
  type User,
} from '@/services/api';

// ──────────────────────── 타입 ────────────────────────

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string, rememberMe: boolean) => Promise<{ success: boolean; error?: string }>;
  signup: (name: string, email: string, password: string, passwordConfirm: string, inviteCode: string) => Promise<{ success: boolean; error?: string }>;
  updateCredentials: (payload: CredentialsUpdatePayload) => Promise<{ success: boolean; error?: string; message?: string }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

// ──────────────────────── Provider ────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // 초기 세션 복원
  useEffect(() => {
    const restore = async () => {
      const restored = await authApi.restoreUser();
      if (restored) setUser(restored);
      setIsLoading(false);
    };
    restore();
  }, []);

  // ────── 로그인 ──────
  const login = useCallback(async (email: string, password: string, rememberMe: boolean) => {
    // 1. 클라이언트 검증
    const emailErr = validators.email(email);
    if (emailErr) return { success: false, error: emailErr };
    const passErr = validators.password(password);
    if (passErr) return { success: false, error: passErr };

    // 2. API 호출
    try {
      const result = await authApi.login({ email: email.trim(), password }, rememberMe);
      setUser(result.user);
      return { success: true };
    } catch (err) {
      if (err instanceof ApiError) {
        return { success: false, error: err.message };
      }
      if (err instanceof NetworkError) {
        return { success: false, error: '네트워크 연결을 확인해주세요' };
      }
      if (err instanceof TimeoutError) {
        return { success: false, error: '서버 응답이 지연되고 있습니다. 잠시 후 다시 시도해주세요' };
      }
      return { success: false, error: '알 수 없는 오류가 발생했습니다' };
    }
  }, []);

  // ────── 회원가입 ──────
  const signup = useCallback(async (
    name: string,
    email: string,
    password: string,
    passwordConfirm: string,
    inviteCode: string,
  ) => {
    // 1. 클라이언트 검증 (순서대로 — 첫 번째 실패에서 중단)
    const nameErr = validators.name(name);
    if (nameErr) return { success: false, error: nameErr };

    const emailErr = validators.email(email);
    if (emailErr) return { success: false, error: emailErr };

    const passErr = validators.password(password);
    if (passErr) return { success: false, error: passErr };

    const confirmErr = validators.passwordConfirm(password, passwordConfirm);
    if (confirmErr) return { success: false, error: confirmErr };

    const codeErr = validators.inviteCode(inviteCode);
    if (codeErr) return { success: false, error: codeErr };

    // 2. API 호출
    try {
      const result = await authApi.signup({
        name: name.trim(),
        email: email.trim(),
        password,
        inviteCode: inviteCode.trim(),
      });
      setUser(result.user);
      return { success: true };
    } catch (err) {
      if (err instanceof ApiError) {
        return { success: false, error: err.message };
      }
      if (err instanceof NetworkError) {
        return { success: false, error: '네트워크 연결을 확인해주세요' };
      }
      if (err instanceof TimeoutError) {
        return { success: false, error: '서버 응답이 지연되고 있습니다. 잠시 후 다시 시도해주세요' };
      }
      return { success: false, error: '알 수 없는 오류가 발생했습니다' };
    }
  }, []);

  // ────── 로그인 아이디·비밀번호 변경 ──────
  const updateCredentials = useCallback(async (payload: CredentialsUpdatePayload) => {
    try {
      const result = await authApi.updateCredentials(payload);
      setUser(result.user);
      return { success: true, message: result.message };
    } catch (err) {
      if (err instanceof ApiError) return { success: false, error: err.message };
      if (err instanceof NetworkError) return { success: false, error: '네트워크 연결을 확인해주세요' };
      if (err instanceof TimeoutError) {
        return { success: false, error: '서버 응답이 지연되고 있습니다. 잠시 후 다시 시도해주세요' };
      }
      return { success: false, error: '로그인 정보를 변경하지 못했습니다. 잠시 후 다시 시도해주세요.' };
    }
  }, []);

  // ────── 로그아웃 ──────
  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // API 실패해도 상태는 초기화
    }
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      isLoading,
      login,
      signup,
      updateCredentials,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

// ──────────────────────── Hook ────────────────────────

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth는 AuthProvider 내부에서만 사용할 수 있습니다');
  }
  return context;
}
